import { useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { forgotPasswordApi } from "../model/authApi";
import { validateEmailWithMessage } from "../utils/auth.utils";
import { toastService } from "@/shared/toast";
import { routes } from "@/app/navigation/routes";

/**
 * Forgot Password ViewModel
 * Manages forgot password form state and business logic
 */
export const useForgotPasswordViewModel = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialEmail = (location.state as { email?: string } | null)?.email ?? "";

  const [email, setEmail] = useState<string>(initialEmail);
  const [emailError, setEmailError] = useState<string | null>(null);
  const BUTTON_ID = "forgot-password-button";

  const mutation = useMutation({
    mutationFn: (email: string) => forgotPasswordApi(email, "inline", BUTTON_ID),
    onSuccess: () => {
      toastService.show(
        "If that email is registered, we've sent instructions to reset your password.",
        "success"
      );
      navigate(routes.login);
    },
    onError: () => {
      toastService.show("Unable to process your request right now. Please try again.", "error");
    },
  });

  const updateEmail = useCallback((newEmail: string) => {
    setEmail(newEmail);
    // Validate email on change (only show error if field has been touched)
    if (newEmail) {
      const validation = validateEmailWithMessage(newEmail);
      setEmailError(validation.error || null);
    } else {
      setEmailError(null);
    }
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      // Validate email before submission
      const validation = validateEmailWithMessage(email);
      setEmailError(validation.error || null);

      if (!validation.isValid) {
        return;
      }

      mutation.mutate(email);
    },
    [email, mutation]
  );

  const reset = useCallback(() => {
    setEmail("");
    setEmailError(null);
  }, []);

  return {
    email,
    isLoading: mutation.isPending,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    emailError,
    updateEmail,
    handleSubmit,
    reset,
  };
};
